import React, { useMemo, useState } from 'react';
import { Btn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, panelPost } from '../../kit';
import { Badge, Check, Labeled, Modal, MultiSelect, inputCls } from '../emailKit';
import { LifecyclePill, PERSONAS, invalidateTagOptions, useTagOptions } from '../../tags/shared';
import { Ago, PEOPLE, SourceRef, emailPeople, fmtDay, num } from './shared';

/**
 * Import people from a CSV or XLSX (people/import_upload → import_preview →
 * import_commit). Four steps:
 *   1. upload — parsed and staged on the server, so the preview and the
 *      import read exactly the same rows;
 *   2. map columns — every field is a picker over the file's columns with the
 *      server's guess preselected, plus the options (tags for everyone, create
 *      missing tags, default persona, what to do with existing accounts, the
 *      source label);
 *   3. preview — every row classified: new, already in the system (with
 *      first added and last activity), duplicate in the file, invalid, team;
 *   4. import — the counts from the preview travel as `expected` and the
 *      server refuses if they moved. New people become Prospects; nobody is
 *      e-mailed.
 */

const MAX_BYTES = 20 * 1024 * 1024;

type Upload = {
  upload_id: string; filename: string; columns: string[]; rows_n: number;
  sample: Record<string, string>[]; guess: Record<string, string | null>;
  targets: Array<{ key: string; label: string }>;
};
type Mode = 'skip' | 'fill_blanks' | 'update';

const MODES: Array<{ key: Mode; label: string; hint: string }> = [
  { key: 'fill_blanks', label: 'Fill blanks', hint: 'Only set fields that are empty on the account now' },
  { key: 'update', label: 'Update', hint: 'Overwrite with every non-empty value from the file' },
  { key: 'skip', label: 'Leave as they are', hint: 'Change nothing on the account; still add the tags' },
];
const TARGET_HINT: Record<string, string> = {
  email: 'required',
  name: 'split into first / last when those are not mapped',
  persona: 'Academia, Industry, Investor or Consultant',
  tags: 'names or keys, separated by , ; or |',
};
const STATUS: Record<string, { label: string; tone: 'green' | 'blue' | 'amber' | 'red' | 'grey' }> = {
  new: { label: 'new', tone: 'green' },
  existing: { label: 'already in system', tone: 'blue' },
  duplicate_in_file: { label: 'duplicate in file', tone: 'amber' },
  invalid: { label: 'invalid', tone: 'red' },
};

const readBase64 = (file: File) => new Promise<string>((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result || '').split(',', 2)[1] || '');
  r.onerror = () => reject(r.error || new Error('Could not read the file'));
  r.readAsDataURL(file);
});

const ImportWizard: React.FC<{ onViewPeople: (s: SourceRef) => void }> = ({ onViewPeople }) => {
  const opts = useTagOptions();
  const [upload, setUpload] = useState<Upload | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [applyTags, setApplyTags] = useState<string[]>([]);
  const [createMissing, setCreateMissing] = useState(false);
  const [persona, setPersona] = useState('');
  const [mode, setMode] = useState<Mode>('fill_blanks');
  const [label, setLabel] = useState('');
  const [step, setStep] = useState<'upload' | 'map' | 'preview' | 'done'>('upload');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [preview, setPreview] = useState<any>(null);
  const [previewBody, setPreviewBody] = useState<Record<string, any> | null>(null);
  const [sampleStatus, setSampleStatus] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState<any>(null);

  const body = useMemo(() => upload && ({
    upload_id: upload.upload_id,
    mapping: Object.fromEntries(Object.entries(mapping).filter(([, c]) => c)),
    apply_tags: applyTags, create_missing_tags: createMissing, default_persona: persona,
    existing_mode: mode, source_label: label.trim(),
  }), [upload, mapping, applyTags, createMissing, persona, mode, label]);

  const reset = () => {
    setUpload(null); setMapping({}); setApplyTags([]); setCreateMissing(false); setPersona('');
    setMode('fill_blanks'); setLabel(''); setPreview(null); setPreviewBody(null); setSampleStatus('');
    setResult(null); setErr(''); setStep('upload');
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setErr('');
    if (file.size > MAX_BYTES) { setErr('The file is larger than 20 MB — split it and import each part.'); return; }
    setBusy('Reading the file…');
    try {
      const content_b64 = await readBase64(file);
      const { data, error } = await panelPost(PEOPLE, 'import_upload', { filename: file.name, content_b64 });
      if (error || !data) { setErr(error || 'Upload failed'); return; }
      const up = data as Upload;
      setUpload(up);
      setMapping(Object.fromEntries(Object.entries(up.guess || {}).filter(([, c]) => c)) as Record<string, string>);
      setLabel(file.name.replace(/\.[^.]+$/, ''));
      setStep('map');
    } catch (e: any) {
      setErr(e?.message || 'Could not read the file');
    } finally {
      setBusy('');
    }
  };

  const runPreview = async (status = '') => {
    if (!body) return;
    setBusy('Checking every row…'); setErr('');
    const { data, error } = await panelPost(PEOPLE, 'import_preview', { ...body, sample_status: status });
    setBusy('');
    if (error || !data) { setErr(error || 'Preview failed'); return; }
    setPreview(data); setPreviewBody(body); setSampleStatus(status); setStep('preview');
  };

  const commit = async () => {
    if (!previewBody || !preview) return;
    setConfirm(false); setBusy('Importing…'); setErr('');
    const { data, error } = await panelPost(PEOPLE, 'import_commit', { ...previewBody, expected: preview.expected });
    setBusy('');
    if (error || !data) { setErr(error || 'Import failed'); return; }
    invalidateTagOptions();
    setResult(data); setStep('done');
  };

  const tagLabel = (k: string) => opts.tagByKey[k]?.label || k;
  const c = preview?.counts || {};
  const modeText = MODES.find(m => m.key === previewBody?.existing_mode);

  return (
    <div className="flex flex-col gap-4">
      {err && <Msg kind="error">{err}</Msg>}
      {busy && <Loading label={busy} />}

      {step === 'upload' && !busy && (
        <Section title="1 · Upload a CSV or Excel file">
          <p className="text-sm text-grayscale-1200">
            Any list of people with an e-mail column: an event sign-up sheet, a CRM export, the PIEMAT database.
            You choose which columns to import next. Nothing is imported and nobody is e-mailed until you confirm.
          </p>
          <input type="file" accept=".csv,.tsv,.txt,.xlsx,.xlsm"
            onChange={e => { onFile(e.target.files?.[0]); e.target.value = ''; }}
            className="text-sm file:mr-3 file:py-1.5 file:px-3.5 file:rounded-full file:border file:border-grayscale-400 file:bg-white file:text-grayscale-1200 file:text-sm" />
          <div className="text-xs text-grayscale-1100">
            CSV (comma, semicolon, tab or pipe; UTF-8 or Excel encodings) or .xlsx (first sheet) · up to 20 MB and 20,000 rows ·
            the first non-empty row is the header.
          </div>
        </Section>
      )}

      {step === 'map' && upload && !busy && (
        <>
          <Section title={`2 · Choose the columns — ${upload.filename} (${num(upload.rows_n)} rows)`}
            right={<Btn small onClick={reset}>Start over</Btn>}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {upload.targets.map(t => (
                <Labeled key={t.key} label={t.label} hint={TARGET_HINT[t.key]}>
                  <Select value={mapping[t.key] || ''}
                    onChange={v => setMapping(m => ({ ...m, [t.key]: v }))}
                    options={['', ...upload.columns]} labels={{ '': '— not imported —' }} />
                </Labeled>
              ))}
            </div>
            {!mapping.email && <Msg kind="error">Pick the column that holds the e-mail address.</Msg>}
            <div className="text-xs text-grayscale-1100">The first rows of the file:</div>
            <DataTable rows={upload.sample.slice(0, 5)} columns={upload.columns} maxHeight="14rem" />
          </Section>

          <Section title="Options">
            <div className="flex flex-wrap items-end gap-4">
              <MultiSelect label="Tags to add to everyone in the file" options={opts.tags.map(t => t.key)}
                labels={Object.fromEntries(opts.tags.map(t => [t.key, `${t.grp ? `${t.grp} › ` : ''}${t.label}`]))}
                values={applyTags} onChange={setApplyTags}
                placeholder={opts.tags.length ? 'None' : 'No tags yet'} />
              <Labeled label="Default persona" hint="when the file has none">
                <Select value={persona} onChange={setPersona}
                  options={['', ...PERSONAS.filter(p => p.key !== 'none').map(p => p.key)]}
                  labels={{ '': 'None', ...Object.fromEntries(PERSONAS.map(p => [p.key, p.label])) }} />
              </Labeled>
              <Labeled label="Source label" hint="shown as “added via”">
                <input value={label} maxLength={120} onChange={e => setLabel(e.target.value)}
                  placeholder="e.g. PIEMAT Oct 2026" className={`${inputCls} min-w-[16rem]`} />
              </Labeled>
            </div>
            {mapping.tags && (
              <Check checked={createMissing} onChange={setCreateMissing}>
                Create tags from the file that do not exist yet (they go in the “Imports” group)
              </Check>
            )}
            <div className="flex flex-col gap-1 text-xs text-grayscale-1100">
              People who already have an account
              <div className="flex flex-wrap gap-x-5 gap-y-1">
                {MODES.map(m => (
                  <label key={m.key} className="flex items-center gap-2 text-sm text-grayscale-1200 cursor-pointer" title={m.hint}>
                    <input type="radio" name="existing_mode" className="accent-primary-500"
                      checked={mode === m.key} onChange={() => setMode(m.key)} />
                    {m.label} <span className="text-[11px] text-grayscale-1100">— {m.hint}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Btn kind="primary" disabled={!mapping.email} onClick={() => runPreview()}>Preview the import</Btn>
            </div>
          </Section>
        </>
      )}

      {step === 'preview' && preview && !busy && (
        <>
          <Section title={`3 · Preview — ${preview.filename}`}
            right={<div className="flex gap-2">
              <Btn small onClick={() => setStep('map')}>Back to the columns</Btn>
              <Btn small onClick={reset}>Start over</Btn>
            </div>}>
            <KPIRow>
              <KPI label="Rows in the file" value={num(c.rows)}
                active={!sampleStatus} onClick={() => runPreview('')} />
              <KPI label="New people" value={num(c.new)} hint="become Prospects"
                active={sampleStatus === 'new'} onClick={() => runPreview('new')} />
              <KPI label="Already in the system" value={num(c.existing)}
                hint={`${num(c.existing_changed)} would change`}
                active={sampleStatus === 'existing'} onClick={() => runPreview('existing')} />
              <KPI label="Duplicates in the file" value={num(c.duplicate_in_file)}
                active={sampleStatus === 'duplicate_in_file'} onClick={() => runPreview('duplicate_in_file')} />
              <KPI label="Invalid" value={num(c.invalid)}
                active={sampleStatus === 'invalid'} onClick={() => runPreview('invalid')} />
              <KPI label="Team addresses" value={num(c.team)}
                active={sampleStatus === 'team'} onClick={() => runPreview('team')} />
            </KPIRow>
            {(preview.warnings || []).length > 0 && (
              <ul className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 list-disc pl-6">
                {preview.warnings.map((w: string, i: number) => <li key={i}>{w}</li>)}
              </ul>
            )}
            {(preview.unknown_tags || []).length > 0 && (
              <div className="text-xs text-grayscale-1200 flex flex-wrap gap-1 items-center">
                <span>Tags in the file that do not exist yet:</span>
                {preview.unknown_tags.map((u: any) => (
                  <Badge key={u.label} tone={u.would_create ? 'green' : 'grey'}>
                    {u.label} ({num(u.n)}) {u.would_create ? '· will be created' : u.reason ? `· ${u.reason}` : '· skipped'}
                  </Badge>
                ))}
              </div>
            )}
            <div className="text-xs text-grayscale-1100">
              {sampleStatus ? `Rows that are ${STATUS[sampleStatus]?.label || 'team addresses'}` : 'The first rows'}
              {' '}— {num(Math.min(preview.sample.length, preview.sample_total))} of {num(preview.sample_total)} shown.
            </div>
            <DataTable rows={preview.sample} maxHeight="26rem" empty="No rows of this kind."
              columns={['line', 'status', 'email', 'first_name', 'last_name', 'organisation', 'designation',
                'country', 'persona_label', 'tags', 'detail', 'lifecycle', 'created_at', 'last_activity_at']}
              labels={{ line: 'Row', persona_label: 'Persona', detail: 'What happens', lifecycle: 'State now',
                created_at: 'First added', last_activity_at: 'Last activity' }}
              render={{
                status: r => (
                  <span className="inline-flex gap-1">
                    <Badge tone={STATUS[r.status]?.tone || 'grey'}>{STATUS[r.status]?.label || r.status}</Badge>
                    {r.team && <Badge tone="grey">team</Badge>}
                  </span>
                ),
                tags: r => [...(r.tags || []), ...(r.unknown_tags || []).map((t: string) => `${t}*`)].join(', ') || '—',
                detail: r => r.error || r.detail || '—',
                lifecycle: r => (r.lifecycle ? <LifecyclePill value={r.lifecycle} /> : '—'),
                created_at: r => (r.created_at ? fmtDay(r.created_at) : '—'),
                last_activity_at: r => <Ago value={r.last_activity_at} empty={r.user_id ? 'none' : '—'} />,
              }} />
            <div className="text-[11px] text-grayscale-1100">* a tag that does not exist yet.</div>
          </Section>

          <Section title="4 · Import">
            <ul className="text-sm text-grayscale-1200 list-disc pl-5">
              <li>Create <b>{num(c.new)}</b> new account(s) — Prospects who have never signed in. <b>No e-mail is sent.</b></li>
              <li>{num(c.existing)} existing account(s): {modeText?.label.toLowerCase()} ({num(c.existing_changed)} would change).</li>
              {(previewBody?.apply_tags || []).length > 0 && (
                <li>Tag everyone with {previewBody!.apply_tags.map((k: string) => `“${tagLabel(k)}”`).join(', ')}.</li>
              )}
              <li>Source: “{previewBody?.source_label || `${preview.filename} (today)`}”.</li>
            </ul>
            <div>
              <Btn kind="primary" disabled={!c.new && !c.existing} onClick={() => setConfirm(true)}>
                Import {num(c.new + c.existing)} people
              </Btn>
            </div>
          </Section>
        </>
      )}

      {step === 'done' && result && (
        <Section title={`Imported — “${result.source_label}”`} right={<Btn small onClick={reset}>Import another file</Btn>}>
          <Msg kind="ok">
            {num(result.created)} new account(s) created, {num(result.updated)} existing updated,
            {' '}{num(result.counts?.unchanged)} left unchanged
            {result.tags_assigned ? `; ${num(result.tags_assigned)} tag assignment(s) added` : ''}
            {(result.tags_created || []).length ? `; created tags ${result.tags_created.map((t: any) => `“${t.label}”`).join(', ')}` : ''}.
          </Msg>
          {(result.tag_errors || []).length > 0 && (
            <Msg kind="error">Some tags could not be applied: {result.tag_errors.join('; ')}</Msg>
          )}
          <div className="flex gap-2 flex-wrap">
            <Btn kind="primary" onClick={() => onViewPeople({ id: result.batch_id, label: result.source_label })}>
              View these people
            </Btn>
            <Btn onClick={() => emailPeople(result.user_ids || [])}>E-mail them ({num((result.user_ids || []).length)})</Btn>
          </div>
        </Section>
      )}

      {confirm && preview && (
        <Modal title={`Import ${num(c.new + c.existing)} people?`} onClose={() => setConfirm(false)}
          footer={<>
            <Btn onClick={() => setConfirm(false)}>Cancel</Btn>
            <Btn kind="primary" onClick={commit}>Import</Btn>
          </>}>
          <p>
            {num(c.new)} new Prospect account(s) will be created and {num(c.existing)} existing one(s) matched
            ({modeText?.label.toLowerCase()}). Nobody is e-mailed.
          </p>
          <p className="text-[11px] text-grayscale-1100">
            The server checks the file again and refuses if these numbers changed since the preview
            (for example, someone signed up meanwhile) — preview again if that happens.
          </p>
        </Modal>
      )}
    </div>
  );
};

export default ImportWizard;
